/**
 * F14.1 — INDICADOR GLOBAL DE CONEXIÓN Y SINCRONIZACIÓN, CENTRO DE SINCRONIZACIÓN Y SALIDA CON PENDIENTES
 *
 * Sin entrada nueva en el menú: un indicador discreto en la cabecera (móvil) y en el pie de la barra lateral
 * (escritorio) — «● En línea», «● Sin conexión», «3 pendientes», «1 necesita revisión» — que abre el centro de
 * sincronización. La conexión es la REAL (`pwa.ts::useConexionReal`, comprobada contra el servidor), no sólo
 * `navigator.onLine`.
 *
 * Los recuentos se leen del índice en claro de la cola (`db.ts::contarColaSinDescifrar`): funcionan aunque el perfil
 * offline esté bloqueado, sin descifrar nada. El detalle (qué operación, por qué falló) sí exige el perfil
 * desbloqueado con su PIN.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { Usuario } from '../api.ts';
import {
  contarColaSinDescifrar,
  listarMutacionesNoSincronizadas,
  listarMutacionesSincronizadasRecientes,
  type Mutacion,
  type RecuentoCola,
} from '../offline/db.ts';
import { presentarEstado } from '../offline/estados-sync.ts';
import { rotuloOperacion, suscribirCambiosCola } from '../offline/operaciones.ts';
import { tieneCapacidadOffline } from '../offline/paquete.ts';
import { buscarPerfilPorFingerprint, fingerprintDeUsuario, hayPerfilDesbloqueado, perfilActivo, useEstadoBloqueoOffline } from '../offline/perfil.ts';
import { sincronizarPendientes, ultimaSincronizacionDe, ultimaSincronizacionSinIdentidad, useEstadoSync } from '../offline/sync.ts';
import { AvisoPerfilDeOtroUsuario } from './aviso-perfil-otro-usuario.tsx';
import { comprobarConexion, useConexionReal, useConectividad, type EstadoConexion } from '../pwa.ts';
import { fechaHora, Modal } from './base.tsx';

const VACIO: RecuentoCola = { pendientes: 0, errores: 0, necesitanRevision: 0 };

/**
 * El perfil offline de ESTE usuario en ESTE dispositivo (por huella), si lo tiene. Sin usuario (recarga sin red: la
 * sesión no se puede comprobar y se trabaja con el perfil desbloqueado por PIN), el perfil activo.
 */
/** El perfil offline de un usuario, consultado en el momento (sin sesión cargada: el perfil desbloqueado, si lo hay). */
async function perfilOfflineDe(usuario: Usuario | null): Promise<string | null> {
  if (usuario === null) return perfilActivo()?.perfilId ?? null;
  if (!tieneCapacidadOffline(usuario.roles) || typeof indexedDB === 'undefined') return null;
  try {
    return (await buscarPerfilPorFingerprint(await fingerprintDeUsuario(usuario.id)))?.perfilId ?? null;
  } catch {
    return null;
  }
}

export function usePerfilOfflineDe(usuario: Usuario | null): string | null {
  const [perfilId, setPerfilId] = useState<string | null>(null);
  const bloqueo = useEstadoBloqueoOffline();
  useEffect(() => {
    let vivo = true;
    void perfilOfflineDe(usuario).then((p) => {
      if (vivo) setPerfilId(p);
    });
    return () => {
      vivo = false;
    };
  }, [usuario, bloqueo]);
  return perfilId;
}

/** Cuántos cambios hay en este dispositivo sin llegar al servidor, para un usuario (en el momento de preguntarlo). */
export async function contarSinSincronizarDe(usuario: Usuario | null): Promise<number> {
  if (usuario === null || !tieneCapacidadOffline(usuario.roles) || typeof indexedDB === 'undefined') return 0;
  try {
    const p = await buscarPerfilPorFingerprint(await fingerprintDeUsuario(usuario.id));
    if (p === null) return 0;
    const r = await contarColaSinDescifrar(p.perfilId);
    return r.pendientes + r.errores + r.necesitanRevision;
  } catch {
    return 0;
  }
}

/** Recuento de la cola, al día: se relee cuando la cola cambia, cuando termina una ronda y cada 15 s. */
export function useRecuentoCola(perfilId: string | null): { recuento: RecuentoCola; releer: () => void } {
  const [recuento, setRecuento] = useState<RecuentoCola>(VACIO);
  const sync = useEstadoSync();
  const releer = useCallback(() => {
    if (perfilId === null) {
      setRecuento(VACIO);
      return;
    }
    void contarColaSinDescifrar(perfilId).then(setRecuento).catch(() => setRecuento(VACIO));
  }, [perfilId]);
  useEffect(() => {
    releer();
    const quitar = suscribirCambiosCola(releer);
    const t = setInterval(releer, 15_000);
    return () => {
      quitar();
      clearInterval(t);
    };
  }, [releer, sync]);
  return { recuento, releer };
}

function textoConexion(e: EstadoConexion, navegadorOnline: boolean): { texto: string; clase: string } {
  if (e === 'SIN_RED' || (e === 'DESCONOCIDO' && !navegadorOnline)) return { texto: 'Sin conexión', clase: 'sin-conexion' };
  if (e === 'SERVIDOR_NO_DISPONIBLE') return { texto: 'Sin conexión con el servidor', clase: 'sin-servidor' };
  return { texto: 'En línea', clase: 'en-linea' };
}

const plural = (n: number, uno: string, varios: string): string => `${String(n)} ${n === 1 ? uno : varios}`;

export function IndicadorSync({ usuario, compacto = false }: { usuario: Usuario | null; compacto?: boolean }): ReactNode {
  const conexion = useConexionReal();
  const conectividad = useConectividad();
  const perfilId = usePerfilOfflineDe(usuario);
  const { recuento, releer } = useRecuentoCola(perfilId);
  const sincronizando = useEstadoSync() === 'SINCRONIZANDO';
  const [abierto, setAbierto] = useState(false);

  const c = textoConexion(conexion, conectividad === 'ONLINE');
  const capaz = perfilId !== null || (usuario !== null && tieneCapacidadOffline(usuario.roles));
  //  Quien no trabaja offline sólo ve el indicador cuando hay algo que decirle (sin conexión).
  if (!capaz && c.clase === 'en-linea') return null;

  const partes: string[] = [];
  if (sincronizando) partes.push('Sincronizando…');
  else {
    if (recuento.pendientes > 0) partes.push(plural(recuento.pendientes, 'pendiente', 'pendientes'));
    if (recuento.errores > 0) partes.push(plural(recuento.errores, 'con error', 'con error'));
    if (recuento.necesitanRevision > 0) partes.push(plural(recuento.necesitanRevision, 'necesita revisión', 'necesitan revisión'));
  }
  const etiqueta = `${c.texto}${partes.length > 0 ? ` · ${partes.join(' · ')}` : ''}`;

  return (
    <>
      <button
        type="button"
        className={`indicador-sync ${c.clase}${recuento.necesitanRevision > 0 ? ' con-revision' : ''}${compacto ? ' compacto' : ''}`}
        aria-label={`${etiqueta}. Abrir el centro de sincronización`}
        title={etiqueta}
        onClick={() => setAbierto(true)}
      >
        <span className="indicador-sync-punto" aria-hidden="true" />
        <span className="indicador-sync-texto">{compacto && partes.length > 0 ? partes.join(' · ') : etiqueta}</span>
      </button>
      {abierto && (
        <CentroSincronizacion
          perfilId={perfilId}
          recuento={recuento}
          conexion={conexion}
          conectividad={conectividad}
          cerrar={() => {
            setAbierto(false);
            releer();
          }}
        />
      )}
    </>
  );
}

function CentroSincronizacion({
  perfilId,
  recuento,
  conexion,
  conectividad,
  cerrar,
}: {
  perfilId: string | null;
  recuento: RecuentoCola;
  conexion: EstadoConexion;
  conectividad: 'ONLINE' | 'OFFLINE';
  cerrar: () => void;
}): ReactNode {
  const desbloqueado = perfilId !== null && hayPerfilDesbloqueado(perfilId);
  const sincronizando = useEstadoSync() === 'SINCRONIZANDO';
  const [detalle, setDetalle] = useState<{ activas: Mutacion[]; recientes: Mutacion[] } | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [deOtroUsuario, setDeOtroUsuario] = useState(false);
  const [comprobando, setComprobando] = useState(false);

  const cargar = useCallback(() => {
    if (!desbloqueado || perfilId === null) return;
    void Promise.all([listarMutacionesNoSincronizadas(perfilId), listarMutacionesSincronizadasRecientes(perfilId, 8)])
      .then(([activas, recientes]) => setDetalle({ activas, recientes }))
      .catch(() => setDetalle(null));
  }, [desbloqueado, perfilId]);
  useEffect(() => {
    cargar();
    return suscribirCambiosCola(cargar);
  }, [cargar]);

  const c = textoConexion(conexion, conectividad === 'ONLINE');
  const enLinea = c.clase === 'en-linea';
  const ultima = perfilId === null ? null : ultimaSincronizacionDe(perfilId);

  async function sincronizarAhora(): Promise<void> {
    if (perfilId === null) return;
    setMensaje(null);
    setDeOtroUsuario(false);
    const r = await sincronizarPendientes(perfilId, { manual: true });
    //  F14.2 — el perfil desbloqueado no es el del usuario de la sesión: no se ha enviado nada.
    if (r === null && ultimaSincronizacionSinIdentidad() === 'OTRO_USUARIO') setDeOtroUsuario(true);
    else if (r === null) setMensaje(enLinea ? 'No había nada que enviar, o el servidor no ha respondido.' : 'Sin conexión: se enviará al recuperarla.');
    else {
      const partes = [`${String(r.aplicadas + r.yaAplicadas)} guardada${r.aplicadas + r.yaAplicadas === 1 ? '' : 's'} en el servidor`];
      if (r.conConflicto + r.rechazadas > 0) partes.push(`${String(r.conConflicto + r.rechazadas)} necesita${r.conConflicto + r.rechazadas === 1 ? '' : 'n'} revisión`);
      if (r.errorTemporal > 0) partes.push(`${String(r.errorTemporal)} con error temporal`);
      if (r.omitidas > 0) partes.push(`${String(r.omitidas)} esperando otra operación`);
      setMensaje(partes.join(' · '));
    }
    cargar();
  }

  const grupos: { titulo: string; filas: Mutacion[] }[] = detalle === null ? [] : [
    { titulo: 'Necesitan revisión', filas: detalle.activas.filter((m) => presentarEstado(m).estado === 'NECESITA_REVISION') },
    { titulo: 'Con error temporal', filas: detalle.activas.filter((m) => presentarEstado(m).estado === 'ERROR_TEMPORAL') },
    { titulo: 'Esperando otra operación', filas: detalle.activas.filter((m) => presentarEstado(m).estado === 'ESPERANDO_OTRA') },
    { titulo: 'Pendientes de sincronizar', filas: detalle.activas.filter((m) => presentarEstado(m).estado === 'PENDIENTE') },
  ];

  return (
    <Modal
      titulo="Sincronización"
      cerrar={cerrar}
      pie={
        <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          {desbloqueado && (
            <button type="button" className="principal" disabled={sincronizando || !enLinea} onClick={() => void sincronizarAhora()}>
              {sincronizando ? 'Sincronizando…' : recuento.errores > 0 ? 'Reintentar' : 'Sincronizar ahora'}
            </button>
          )}
        </div>
      }
    >
      <div className="centro-sync">
        <p className="fila" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className={`indicador-sync estatico ${c.clase}`}><span className="indicador-sync-punto" aria-hidden="true" />{c.texto}</span>
          <button
            type="button"
            className="pequeno"
            disabled={comprobando}
            onClick={() => {
              setComprobando(true);
              void comprobarConexion().finally(() => setComprobando(false));
            }}
          >
            {comprobando ? 'Comprobando…' : 'Comprobar ahora'}
          </button>
        </p>
        <p className="suave pequena">
          Última sincronización: {ultima === null ? 'todavía ninguna en este dispositivo' : fechaHora(ultima.en)}
        </p>

        <div className="cifras">
          <div className="cifra"><div className="n">{recuento.pendientes}</div><div className="t">Pendientes</div></div>
          <div className="cifra"><div className="n">{recuento.errores}</div><div className="t">Con error temporal</div></div>
          <div className="cifra"><div className="n">{recuento.necesitanRevision}</div><div className="t">Necesitan revisión</div></div>
        </div>

        {perfilId === null && <p className="suave">Este dispositivo no tiene trabajo guardado sin conexión.</p>}
        {perfilId !== null && !desbloqueado && (
          <p className="nota info">
            Los cambios están guardados y cifrados en este dispositivo. Para ver el detalle o sincronizarlos, <Link to="/offline" onClick={cerrar}>desbloquea con tu PIN</Link>.
          </p>
        )}
        {mensaje !== null && <p className="nota" role="status">{mensaje}</p>}
        {deOtroUsuario && <AvisoPerfilDeOtroUsuario />}
        {!enLinea && desbloqueado && recuento.pendientes + recuento.errores > 0 && (
          <p className="nota aviso">Sin conexión con el servidor: los cambios siguen guardados en este dispositivo y se enviarán al recuperarla.</p>
        )}

        {grupos.filter((g) => g.filas.length > 0).map((g) => (
          <section key={g.titulo} aria-label={g.titulo} className="centro-sync-grupo">
            <h3 className="pequena">{g.titulo} ({g.filas.length})</h3>
            <ul className="centro-sync-lista">
              {g.filas.map((m) => {
                const e = presentarEstado(m);
                return (
                  <li key={m.idOperacionCliente}>
                    <span>{rotuloOperacion(m)}</span> <span className={`etiqueta ${e.clase}`}>{e.texto}</span>
                    <div className="suave pequena">Guardada {fechaHora(m.creadoLocalEn)}{m.ultimoError !== null && ` · ${m.ultimoError.mensaje}`}</div>
                  </li>
                );
              })}
            </ul>
            {g.titulo === 'Necesitan revisión' && <Link to="/offline/revision" onClick={cerrar}>Revisar y decidir qué hacer</Link>}
          </section>
        ))}

        {detalle !== null && detalle.recientes.length > 0 && (
          <section aria-label="Guardadas recientemente en el servidor" className="centro-sync-grupo">
            <h3 className="pequena">Guardadas en el servidor recientemente</h3>
            <ul className="centro-sync-lista">
              {detalle.recientes.map((m) => (
                <li key={m.idOperacionCliente}>
                  <span>{rotuloOperacion(m)}</span> <span className="etiqueta exito-suave">Guardado en el servidor</span>
                  <div className="suave pequena">Guardada {fechaHora(m.creadoLocalEn)}</div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Modal>
  );
}

/**
 * F14.1 — cerrar sesión con cambios sin enviar: nunca en silencio. «Cerrar sesión igualmente» no pierde nada: el
 * perfil se bloquea y sus cambios siguen cifrados en el dispositivo, esperando al mismo usuario y su PIN.
 */
export function useSalidaConPendientes(usuario: Usuario | null, salir: () => Promise<void>): { pedirSalida: () => void; dialogo: ReactNode } {
  const perfilId = usePerfilOfflineDe(usuario);
  const navegar = useNavigate();
  const [pendientes, setPendientes] = useState<RecuentoCola | null>(null);
  const [sincronizando, setSincronizando] = useState(false);

  //  Se resuelve el perfil AL PULSAR, no con el del hook: si «Salir» se pulsa antes de que el hook lo haya
  //  encontrado, se saldría sin avisar con cambios pendientes.
  async function contar(): Promise<RecuentoCola> {
    const id = perfilId ?? (await perfilOfflineDe(usuario));
    if (id === null) return VACIO;
    try {
      return await contarColaSinDescifrar(id);
    } catch {
      return VACIO;
    }
  }

  const pedirSalida = (): void => {
    void (async () => {
      const r = await contar();
      if (r.pendientes + r.errores + r.necesitanRevision === 0) await salir();
      else setPendientes(r);
    })();
  };

  const total = pendientes === null ? 0 : pendientes.pendientes + pendientes.errores + pendientes.necesitanRevision;
  const desbloqueado = perfilId !== null && hayPerfilDesbloqueado(perfilId);
  const dialogo = pendientes === null ? null : (
    <Modal
      titulo="Hay cambios sin sincronizar"
      cerrar={() => setPendientes(null)}
      pie={
        <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          {desbloqueado ? (
            <button
              type="button"
              className="principal"
              disabled={sincronizando}
              onClick={() => {
                if (perfilId === null) return;
                setSincronizando(true);
                void sincronizarPendientes(perfilId, { manual: true })
                  .then(async () => {
                    const r = await contar();
                    if (r.pendientes + r.errores + r.necesitanRevision === 0) {
                      setPendientes(null);
                      await salir();
                    } else setPendientes(r);
                  })
                  .finally(() => setSincronizando(false));
              }}
            >
              {sincronizando ? 'Sincronizando…' : 'Sincronizar'}
            </button>
          ) : (
            <button type="button" className="principal" onClick={() => { setPendientes(null); navegar('/offline'); }}>Sincronizar</button>
          )}
          <button type="button" onClick={() => { setPendientes(null); void salir(); }}>Cerrar sesión igualmente</button>
          <button type="button" onClick={() => setPendientes(null)}>Cancelar</button>
        </div>
      }
    >
      <p>
        Hay {plural(total, 'cambio pendiente', 'cambios pendientes')} de sincronizar
        {pendientes.necesitanRevision > 0 ? ` (${plural(pendientes.necesitanRevision, 'necesita', 'necesitan')} revisión)` : ''}.
      </p>
      <p className="suave pequena">
        Si cierras sesión igualmente, no se pierden: quedan guardados y cifrados en este dispositivo y se enviarán cuando
        vuelvas a entrar con tu usuario y tu PIN.
      </p>
    </Modal>
  );
  return { pedirSalida, dialogo };
}
